import {
  Mail,
  Phone,
  Building,
  Globe,
  Calendar,
  Clock,
  Shield,
  Lock,
  BadgeCheck,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import type { UserDetail } from "@/types/admin";
import { formatDate, formatDateTime } from "@/lib/utils";

interface UserDetailCardProps {
  user: UserDetail;
}

export function UserDetailCard({ user }: UserDetailCardProps) {
  if (!user) {
    return null;
  }

  const isActive = user.accountStatus === "Active";
  const displayName =
    user.fullName ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.email ||
    "Unknown User";

  const roleColors: Record<string, string> = {
    SuperAdmin: "bg-brand-soft text-brand-ink",
    BillingAdmin: "bg-info-soft text-info-ink",
    SupportAdmin: "bg-ok-soft text-ok-ink",
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{displayName}</CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {user.accountStatus && user.accountStatus.trim() && (
                <Badge variant={isActive ? "default" : "destructive"}>
                  {user.accountStatus}
                </Badge>
              )}
              {user.role && user.role.trim() && (
                <Badge
                  className={
                    roleColors[user.role] || "bg-muted text-muted-foreground"
                  }
                >
                  {user.role}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Contact Information */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
            Contact Information
          </h3>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{user.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>{user.phoneNumber || "—"}</span>
            </div>
            {user.company && (
              <div className="flex items-center gap-2 text-sm">
                <Building className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{user.company}</span>
              </div>
            )}
            {user.country && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{user.country}</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Security & Verification Status */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
            Security & Verification
          </h3>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4 shrink-0" />
                <span>Email Verification</span>
              </div>
              <Badge
                variant={user.emailVerified ? "default" : "outline"}
                className={
                  user.emailVerified
                    ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 border-emerald-500/30 gap-1"
                    : "text-muted-foreground gap-1"
                }
              >
                {user.emailVerified ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                {user.emailVerified ? "Verified" : "Unverified"}
              </Badge>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-4 w-4 shrink-0" />
                <span>Phone Verification</span>
              </div>
              <Badge
                variant={user.phoneVerified ? "default" : "outline"}
                className={
                  user.phoneVerified
                    ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 border-emerald-500/30 gap-1"
                    : "text-muted-foreground gap-1"
                }
              >
                {user.phoneVerified ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                {user.phoneVerified ? "Verified" : "Unverified"}
              </Badge>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Lock className="h-4 w-4 shrink-0" />
                <span>Two-Factor Auth (2FA)</span>
              </div>
              <Badge
                variant={user.twoFactorEnabled ? "default" : "outline"}
                className={
                  user.twoFactorEnabled
                    ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 border-emerald-500/30 gap-1"
                    : "text-muted-foreground gap-1"
                }
              >
                {user.twoFactorEnabled ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                {user.twoFactorEnabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            {user.verificationLevel && (
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <BadgeCheck className="h-4 w-4 shrink-0" />
                  <span>KYC Level</span>
                </div>
                <Badge variant="secondary" className="font-semibold gap-1">
                  <BadgeCheck className="h-3.5 w-3.5 text-brand-ink" />
                  {user.verificationLevel}
                </Badge>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Account Details */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
            Account Details
          </h3>
          <div className="grid gap-3">
            <div className="flex items-start gap-2 text-sm">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <div className="font-medium">Role</div>
                <div className="text-muted-foreground">{user.role}</div>
              </div>
            </div>
            {user.createdAt && (
              <div className="flex items-start gap-2 text-sm">
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <div className="font-medium">Created</div>
                  <div className="text-muted-foreground">
                    {formatDate(user.createdAt)}
                  </div>
                </div>
              </div>
            )}
            {user.lastLoginAt && (
              <div className="flex items-start gap-2 text-sm">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <div className="font-medium">Last Login</div>
                  <div className="text-muted-foreground">
                    {formatDateTime(user.lastLoginAt)}
                    {user.lastLoginIp && (
                      <span className="ml-1 text-xs text-muted-foreground/80">
                        ({user.lastLoginIp})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {user.updatedAt && (
              <div className="flex items-start gap-2 text-sm">
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <div className="font-medium">Updated</div>
                  <div className="text-muted-foreground">
                    {formatDate(user.updatedAt)}
                  </div>
                </div>
              </div>
            )}
            {(user.timezone || user.language) && (
              <div className="flex items-start gap-2 text-sm">
                <Globe className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <div className="font-medium">Locale & Timezone</div>
                  <div className="text-muted-foreground">
                    {[user.language, user.timezone].filter(Boolean).join(" • ")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
